/**
 * 学习知识库客户端 API（ziwei-knowledge 服务，与 userCases 同服务同信封）
 * - 目录 GET /api/v1/learn/chapters：公开，摘要级字段（effective_level/preview 供锁标记；不下发正文）
 * - 详情 GET /api/v1/learn/chapter/:id：free 直接全文；付费需网关注入 X-Subscription-Level，
 *   未达标 → HTTP 403 + code 40301（MembershipRequiredError）
 * - 图片 /api/v1/learn/files/* 公开；正文图片 URL 经 rewriteContent 幂等改写
 *   （dev 库存 URL 无前缀 → 补 KNOWLEDGE_BASE；生产已带前缀 → 原样）
 * - 登录态（store.token）存在时自动带 Bearer，供网关派生订阅等级
 */
import { KNOWLEDGE_BASE } from './userCases';

/** 目录摘要（客户端列表/树渲染） */
export interface LearnChapterSummary {
  id: number;
  title: string;
  category: string;
  parent_id: number;
  access_level: string;
  /** 沿父链继承后的有效权益等级：free/basic/pro */
  effective_level: string;
  /** 锁态展示用预览正文（Markdown） */
  preview: string;
  version: number;
  sort: number;
}

/** 章节详情（全文 Markdown） */
export interface LearnChapterDetail {
  id: number;
  title: string;
  access_level: string;
  effective_level: string;
  content: string;
  version: number;
}

/** 付费章节权益未达标（403 code 40301） */
export class MembershipRequiredError extends Error {
  constructor() {
    super('membership required');
  }
}

/** 统一响应信封（code=0 成功；错误文案兼容 msg/message 两种口径） */
interface Envelope<T> {
  code: number;
  data: T;
  msg?: string;
  message?: string;
}

/** 请求封装：统一信封解析；40301 → MembershipRequiredError */
async function request<T>(url: string, token?: string | null): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (e) {
    throw new Error(`网络请求失败：${e instanceof Error ? e.message : String(e)}`);
  }
  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    // 非 JSON 响应
  }
  const env = json as Envelope<T> | null;
  if (!env || typeof env.code !== 'number') {
    throw new Error(`响应格式非法（HTTP ${res.status}）`);
  }
  if (env.code === 40301) throw new MembershipRequiredError();
  if (env.code !== 0) throw new Error(env.msg || env.message || `接口错误（code ${env.code}）`);
  return env.data;
}

/** 章节目录（公开；category 可选过滤） */
export function fetchLearnChapters(token?: string | null): Promise<{ items: LearnChapterSummary[] }> {
  return request<{ items: LearnChapterSummary[] }>(`${KNOWLEDGE_BASE}/api/v1/learn/chapters`, token);
}

/** 章节详情（free 全文；付费按权益，未达标抛 MembershipRequiredError） */
export function fetchLearnChapter(id: number, token?: string | null): Promise<LearnChapterDetail> {
  return request<LearnChapterDetail>(`${KNOWLEDGE_BASE}/api/v1/learn/chapter/${id}`, token);
}

/** 正文图片默认路径标记（服务端 DefaultURLPrefix） */
const FILE_MARKER = '/api/v1/learn/files';

/** 生产历史存量前缀（服务端上传时以 URLPrefix=/ziwei-knowledge 写入的相对地址） */
const LEGACY_PREFIX = '/ziwei-knowledge';

/**
 * 正文图片 URL 幂等改写：先归一化剥前缀（绝对前缀 / 历史相对前缀 / 裸路径三种形态），
 * 再统一补当前环境 KNOWLEDGE_BASE；KNOWLEDGE_BASE 为空（直连）时原样返回
 */
export function rewriteContent(md: string): string {
  if (!KNOWLEDGE_BASE || !md.includes(FILE_MARKER)) return md;
  return md
    .split(KNOWLEDGE_BASE + FILE_MARKER)
    .join(FILE_MARKER)
    .split(LEGACY_PREFIX + FILE_MARKER)
    .join(FILE_MARKER)
    .split(FILE_MARKER)
    .join(KNOWLEDGE_BASE + FILE_MARKER);
}
